import { Link, useParams } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { usePlan } from '../plan/usePlan';
import { EntityPage } from './EntityPage';
import { ArcPage } from '../doc/ArcPage';
import type { EntityMode } from './EntityPage';
import { buildVaultIndex } from '../entity/vaultIndex';
import { useTheme } from '../design/useTheme';
import {
  appendToBody,
  planMove,
  reorderList,
  removeMember,
  retitle,
  withEvidence,
  withStatus,
  withVisibility,
} from '../plan/edits';
import { useCallback } from 'react';
import type { VaultEntity } from '../entity/entity';
import { heldBy, innerArcNames } from '../plan/arc';
import type { Arc, Status } from '../plan/arc';
import type { DropTarget } from '../plan/useBoardDrag';
import { Loading } from '../shell/Loading';

export { entityHref as arcHref } from './EntityPage';

/**
 * Route-level only: resolve a name to an entity and hand it to the page.
 * Which blocks appear is the page's business, derived from the file — so
 * pointing this route at a recording would already work, once the vault
 * source hands recordings over the same way it hands over arcs.
 */
export function ArcRoute({ mode = 'log' }: { mode?: EntityMode } = {}) {
  const { arcId } = useParams();
  const { state, write, commit } = useVault();
  const { theme } = useTheme();
  const { plan: planState } = usePlan();
  const plan = planState.status === 'ready' ? planState.plan : null;

  const name = decodeURIComponent(arcId ?? '');
  const entity = plan?.arcs.find((a) => a.name === name);

  // Direct members only, and only the ones the plan actually loaded — a name
  // that resolves to nothing is a broken reference, not a member with no state.
  const byName = new Map((plan?.arcs ?? []).map((a) => [a.name, a]));
  const members = entity
    ? innerArcNames(entity)
        .map((n) => byName.get(n))
        .filter((a): a is NonNullable<typeof a> => Boolean(a))
    : [];

  // The back-link, and it has to be a walk: membership is declared on the
  // HOLDER, so nothing in this arc's own file says what holds it. Null while
  // the plan is still loading, so the rail says "looking…" rather than
  // claiming the arc is in nothing.
  const inArcs = entity && plan ? heldBy(entity, plan.arcs) : null;

  const append = useCallback(
    async (text: string) => {
      if (!entity || state.status !== 'ready') return;
      const change = (t: string) => appendToBody(t, text);
      write([{ path: entity.path, text: change(entity.raw), change }]);
    },
    [entity, state, write],
  );

  /** One click, one file — the same single write the board makes, so the two
   *  surfaces cannot drift on what changing a status means. */
  const setStatus = useCallback(
    async (status: Status, evidence?: string) => {
      if (!entity || state.status !== 'ready') return;
      const change = (t: string) => (evidence ? withEvidence(withStatus(t, status), evidence) : withStatus(t, status));
      await commit([{ path: entity.path, text: change(entity.raw), change }]);
    },
    [entity, state, commit],
  );

  /** A new name is a new title line; the app reads it back as the arc's
   *  name. Sent at once, like a status: it is one discrete act. */
  const rename = useCallback(
    async (next: string) => {
      if (!entity || state.status !== 'ready') return;
      const change = (t: string) => retitle(t, next);
      const text = change(entity.raw);
      if (text !== entity.raw) await commit([{ path: entity.path, text, change }]);
    },
    [entity, state, commit],
  );

  /** Filing this arc away, or taking it back out. Beside [setStatus] rather
   *  than inside it: `visibility:` supersedes what the status says without
   *  overwriting it, which is what makes the sweep reversible. */
  const setArchived = useCallback(
    async (archived: boolean) => {
      if (!entity || state.status !== 'ready') return;
      const change = (t: string) => withVisibility(t, archived ? 'archived' : 'visible');
      await commit([{ path: entity.path, text: change(entity.raw), change }]);
    },
    [entity, state, commit],
  );

  /**
   * Reordering the arcs a storyline holds, from the holds panel.
   *
   * The write is to the file that OWNS the list, which is not always this
   * page's: a storyline expanded in place inside a workspace owns its own, and
   * that is the file whose `arcs:` has to move. The app reads a changed list
   * straight onto its member edges' positions, so this is the member order
   * rather than a view this surface keeps to itself.
   */
  const reorderHeld = useCallback(
    (target: VaultEntity, order: string[]) => {
      if (state.status !== 'ready') return;
      const change = (t: string) => reorderList(t, 'arcs', order);
      const text = change(target.raw);
      if (text !== target.raw) write([{ path: target.path, text, change }]);
    },
    [state, write],
  );

  /** Leaving this storyline is a write to this file only; the member is
   *  untouched. */
  const removeHeld = useCallback(
    async (member: Arc) => {
      if (!entity || state.status !== 'ready') return;
      const edit = removeMember(entity, member.path);
      if (edit) await commit([edit]);
    },
    [entity, state, commit],
  );

  /**
   * A member dragged into another lane, or reordered inside one.
   *
   * The planner is the board's, given only THIS storyline's members — which
   * is what scopes the renumber. Order lives with each arc but means position
   * within its holder, so renumbering against the whole vault's arcs of that
   * status would rewrite the order of arcs this page never showed.
   */
  const moveMember = useCallback(
    async (moved: string, target: DropTarget) => {
      if (state.status !== 'ready') return;
      const edits = planMove(members, moved, target.status, target.beforeName);
      if (edits.length === 0) return;
      write(edits);
    },
    [members, state, write],
  );

  /** A member's own status button. One file, like the holder's own chip. */
  const setMemberStatus = useCallback(
    async (member: Arc, status: Status, evidence?: string) => {
      if (state.status !== 'ready') return;
      const change = (t: string) => (evidence ? withEvidence(withStatus(t, status), evidence) : withStatus(t, status));
      write([{ path: member.path, text: change(member.raw), change }]);
    },
    [state, write],
  );

  const setMemberArchived = useCallback(
    async (member: Arc, archived: boolean) => {
      if (state.status !== 'ready') return;
      const change = (t: string) => withVisibility(t, archived ? 'archived' : 'visible');
      write([{ path: member.path, text: change(member.raw), change }]);
    },
    [state, write],
  );

  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>Not connected</h1>
        <p>
          <Link to="/connect">Connect this browser</Link> to read your vault.
        </p>
      </section>
    );
  }

  if (!plan || state.status !== 'ready') return <Loading title="Opening…" />;

  if (!entity) {
    return (
      <section className="placeholder">
        <h1>Not in the vault</h1>
        <p className="placeholder-phase">{name}</p>
        <p>
          <Link to="/plan">Back to the board</Link>
        </p>
      </section>
    );
  }

  // The arc's own page is the document; its lanes are the older page's.
  if (mode === 'log') {
    return (
      <ArcPage
        arc={entity}
        arcs={plan.arcs}
        onSetStatus={(status, evidence) => void setStatus(status, evidence)}
        onSetArchived={(archived) => void setArchived(archived)}
        onRename={(next) => void rename(next)}
        onRemoveMember={(member) => void removeHeld(member)}
      />
    );
  }

  const byTitle = new Map(plan.arcs.map((a) => [a.title, a]));
  const index = buildVaultIndex(state.vault);

  return (
    <EntityPage
      entity={entity}
      registries={plan.registries}
      theme={theme}
      onAppend={append}
      onSetStatus={(status, evidence) => void setStatus(status, evidence)}
      onSetArchived={(archived) => void setArchived(archived)}
      onRename={(next) => void rename(next)}
      onReorderHeld={reorderHeld}
      mode={mode}
      inArcs={inArcs}
      members={members}
      onMoveMember={(moved, target) => void moveMember(moved, target)}
      onSetMemberStatus={(member, status, evidence) => void setMemberStatus(member, status, evidence)}
      onSetMemberArchived={(member, archived) => void setMemberArchived(member, archived)}
      resolveName={(n) => {
        // An arc we already hold answers with its real title. Anything else
        // — a cited recording, most often — resolves to a path we can link
        // to without opening it, which is what keeps a page one read.
        const loaded = byName.get(n);
        if (loaded) return loaded;
        const path = index.pathFor(n);
        return path ? stub(path) : null;
      }}
      titleOf={(t) => byTitle.get(t) ?? null}
    />
  );
}

/** A reference we can link to without having opened it. */
function stub(path: string): VaultEntity {
  const name = path.replace(/^.*\//, '');
  return { path, name, raw: '', title: name.replace(/\.md$/, ''), frontMatter: {}, body: '' };
}
